// ── Kostenstellen-Registry API client ────────────────────────────────────────
// School Kostenstellen come from /api/schulen (which joins dim.Kostenstelle
// migration 069/092 and surfaces kostenstelleId/Code/Name on every row).
//
// Phase 5 of the Kostenart restructure (2026-06-10): the LEGACY manual source
// (`dim.Kostenstellen` mig 016 via /api/kostenstellen) is RETIRED — the
// endpoint returns 410 and mig176 drops the table. Non-school Kostenstellen
// are the canonical OPERATIVE rows of dim.Kostenstelle (069); read them via
// useOperativeKostenstellen() below. `manual` is kept in the return shape
// (always []) so consumers destructure unchanged.
//
// Phase 1 of Design Z'' (ADR-2026-05-13-01) — kills the client-side
// derivedSchoolKostenstelle that mis-derived codes from HauptSchuleAbkuerzung
// (causing the "4 schools all render KS-GOETHE" bug).

import { useQuery } from '@tanstack/react-query'
import { api } from '../../../../shared/api'
import { useSchulen, type School } from '../../../schulen/api/schulenApi'
import type { Kostenstelle } from '../lib/registryTypes'
import type { DimKostenstelleRow } from './schoolSupplierApi'

const OPERATIVE_KEY = ['dim-kostenstelle-operative'] as const
const COST_KOSTENARTEN_KEY = ['dim-kostenart-cost'] as const

export interface KostenartRow {
  id: number
  code: string
  name: string
  parentId: number | null
}

function schoolToKostenstelle(school: School): Kostenstelle | null {
  // Only return a Kostenstelle if the backend supplied one. Schools without a
  // dim.Kostenstelle row (shouldn't happen post-migration-069, but defensive)
  // are excluded from the registry entirely — they'd otherwise drift back to
  // the old "derived KS-${abkuerzung}" failure mode.
  const schoolId = school.id ?? null
  const ksId = school.kostenstelleId
  const ksCode = school.kostenstelleCode
  if (!schoolId || ksId == null || !ksCode) return null
  return {
    id: `school-${schoolId}`,
    code: ksCode,
    name: school.kostenstelleName ?? school.name ?? '(unbenannte Schule)',
    type: 'school',
    schoolId,
    // Numeric dim.Kostenstelle.id — the real FK key (the string `id` is a slug).
    // Required for the manual-Personalkosten lookup/save.
    kostenstelleId: ksId,
    dimSchoolId: school.dimSchoolId,
    category: 'Schulen',
    active: (school.status?.toLowerCase() ?? 'aktiv') === 'aktiv',
    notes: null,
    createdAt: '',
    updatedAt: '',
  }
}

/**
 * School-Kostenstellen registry, derived from the canonical
 * `dim.Kostenstelle` rows joined on /api/schulen.
 *
 * Phase 5: the legacy manual source (016) is retired — `manual` is always []
 * (kept for destructuring compat). Non-school Kostenstellen live in
 * dim.Kostenstelle kind='operative'; read them via useOperativeKostenstellen().
 *
 * School KS are read-only here; manage them via the admin UI which writes
 * `display_code` directly to dim.Kostenstelle.
 */
export function useKostenstellen() {
  const { data: schools = [], isLoading: schoolsLoading, error: schoolsError } = useSchulen()

  const manual: Kostenstelle[] = []
  const schoolKs = schools
    .map(schoolToKostenstelle)
    .filter((k): k is Kostenstelle => k !== null)
  const all: Kostenstelle[] = schoolKs

  return {
    data: all,
    manual,
    schoolKs,
    isLoading: schoolsLoading,
    error: schoolsError ?? null,
  }
}

/**
 * Canonical OPERATIVE Kostenstellen from `dim.Kostenstelle` (069, BIGINT id),
 * both Firmen, active only. This is the source for the Lieferanten
 * "Feste Kostenstelle" dropdown under the Kostenart restructure (Phase 0):
 * a verwaltung-routed supplier's fixed overhead KS must reference the
 * canonical table, NOT the legacy orphan slug list that `useKostenstellen()`
 * returns. Rows carry `id` (the BIGINT FK), `resolved_code`, `firma`, and
 * `operative_category`.
 */
export function useOperativeKostenstellen() {
  return useQuery({
    queryKey: OPERATIVE_KEY,
    queryFn: () =>
      api.get<DimKostenstelleRow[]>('/api/dim/kostenstelle?kind=operative&active_only=1'),
    staleTime: 60_000,
  })
}

/**
 * Maps a canonical operative dim.Kostenstelle row (DimKostenstelleRow) onto
 * the read-only Kostenstelle registry shape so operative rows can flow
 * through the same table/filter code as school rows on the Auswertung pages
 * (2026-09-17, "Verwaltung fehlt in der Auswertung"). `kostenstelleId` is the
 * field the manual-Personalkosten lookup already keys on (registryApi.ts
 * schoolToKostenstelle does the same for schools) — it's what makes
 * PersonalEditCell work for operative rows with zero further changes.
 */
export function operativeToKostenstelle(row: DimKostenstelleRow): Kostenstelle {
  return {
    id: `ks-${row.id}`,
    code: row.resolved_code,
    name: row.name,
    type: 'operative',
    schoolId: null,
    dimSchoolId: null,
    kostenstelleId: row.id,
    firma: row.firma,
    datevExportCode: row.datev_export_code,
    category: row.operative_category || row.name,
    // The endpoint is called with active_only=1 — every row it returns is
    // already active; no second active_to check needed here.
    active: true,
    notes: null,
    createdAt: '',
    updatedAt: '',
  }
}

/**
 * Active cost Kostenarten from `dim.Kostenart` (kind='cost', active only).
 * Used by the Lieferant edit modal "Standard-Kostenart" dropdown.
 */
export function useCostKostenarten() {
  return useQuery({
    queryKey: COST_KOSTENARTEN_KEY,
    queryFn: () =>
      api.get<KostenartRow[]>('/api/dim/kostenart?kind=cost&active_only=1'),
    staleTime: 5 * 60_000,
  })
}

// Phase 5: useUpsertKostenstelle / useToggleKostenstelleActive removed — they
// wrote the retired 016 table (/api/kostenstellen now answers 410). Operative
// Kostenstellen are canonical dim.Kostenstelle rows; display_code edits go
// through PATCH /api/dim/kostenstelle/{id} (see schoolSupplierApi).
